import React, {cloneElement} from 'react';
import { Clock, Star, TrendingUp, EyeOff, MapPin, CheckCircle2 } from 'lucide-react';
import {motion} from "framer-motion";

const TheDifference = () => {

    const features = [
        {
            id: 1,
            title: "Time Technique Trading™",
            subtitle: "Our Flagship Program",
            icon: <Clock className="w-6 h-6 text-primary" />,
            description: "We read market time cycles, not price fluctuations. Built to identify the 'when' before the 'where'. Predictive and forward-looking.",
            tags: ["Predictive", "Forward-Looking"]
        },
        {
            id: 2,
            title: "Scientific Astro Techniques",
            subtitle: "Institutional Grade",
            icon: <Star className="w-6 h-6 text-primary" />,
            description: "Used quietly by hedge funds and billionaires. We’ve engineered a structured, research-backed system. Zero superstition, 100% data-driven.",
            tags: ["Data-Driven", "No Superstition"]
        },
        {
            id: 3,
            title: "Cycle Forecasting",
            subtitle: "Advanced Precision",
            icon: <TrendingUp className="w-6 h-6 text-primary" />,
            description: "Markets repeat in time. We map high-precision turning points across Forex, Gold, Indices, and Crypto before they occur.",
            tags: ["Global Markets", "High Precision"]
        },
        {
            id: 4,
            title: "100% Non-Indicator Based",
            subtitle: "Ultimate Clarity",
            icon: <EyeOff className="w-6 h-6 text-primary" />,
            description: "No RSI, no EMA, no MACD. Everything we teach is leading, not lagging. Trading without indicators equals ultimate clarity.",
            tags: ["Leading Indicators", "Pure Price"]
        },
        {
            id: 5,
            title: "Made in Dubai",
            subtitle: "Built for the World",
            icon: <MapPin className="w-6 h-6 text-primary" />,
            description: "The only Dubai-based financial academy specializing exclusively in time-based strategies—a rare expertise almost no one teaches globally.",
            tags: ["UAE Based", "Global Reach"]
        }
    ];

    return (
        <section className="relative w-full py-24 px-4 sm:px-8 lg:px-12 xl:px-20 font-poppins overflow-hidden">

            {/* Inner Container */}
            <div className="relative z-10 max-w-7xl mx-auto">

                {/* Section Header */}
                <div className="lg:text-center max-w-3xl mx-auto mb-16 md:mb-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            The Methodology
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-3xl md:text-5xl font-bold text-dark dark:text-light leading-[1.1]"
                    >
                        <span className="block leading-[1.2]">Unlike Any Trading Institute</span>
                        <span className="block leading-[1.2] text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                                in the UAE
                </span>
                    </motion.h2>

                    <p className="text-gray-600 dark:text-gray-400 text-lg">
                        Most teach you how to read the past. We teach you how to <span className="text-dark dark:text-light font-semibold">calculate the future.</span>
                    </p>
                </div>

                {/* Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">

                    {features.map((item, index) => (
                        <div
                            key={item.id}
                            /*
                               FIX 1: Grid Logic
                               - index === 4 is the 5th item.
                               - On MD (Tablet) & LG (iPad Pro): It spans 2 cols (full width at bottom).
                               - On XL (Desktop): It switches to 1 col and centers itself (col-start-2).
                            */
                            className={`
                                group relative p-6 sm:p-8 rounded-2xl transition-all duration-300
                                bg-white/50 dark:bg-bg-dark-2/50 backdrop-blur-sm border border-gray-200 dark:border-white/5
                                hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 dark:hover:border-primary/30
                                flex flex-col
                                ${index === 4 ? "md:col-span-2 xl:col-span-1 xl:col-start-2" : ""}
                            `}
                        >
                            {/* Hover Gradient Overlay */}
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>

                            {/* Icon Box */}
                            <div className="relative mb-6 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white dark:bg-white/10 border border-gray-100 dark:border-white/10 shadow-sm group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                <div className="group-hover:text-white transition-colors duration-300">
                                    {cloneElement(item.icon, { className: "w-7 h-7" })}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="relative flex-1 flex flex-col">
                                <span className="block text-xs font-bold text-primary tracking-wider uppercase mb-2">
                                    {item.subtitle}
                                </span>
                                <h3 className="text-xl font-bold text-dark dark:text-light mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                                    {item.description}
                                </p>

                                {/* Tags - Pushed to bottom */}
                                <div className="flex flex-wrap gap-2 mt-auto">
                                    {item.tags.map((tag, i) => (
                                        <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 shadow-sm">
                                            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-300 uppercase">
                                                {tag}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default TheDifference;